import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Switch,
  Button,
  useToast,
  Spinner,
  Flex,
  FormHelperText,
  Divider
} from '@chakra-ui/react';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';

const IonmGeneralRulesConfig = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Form state
  const [billAttemptedMep, setBillAttemptedMep] = useState(false);
  const [flagMissingPayer, setFlagMissingPayer] = useState(false);
  const [allowDualTrapeziusBilling, setAllowDualTrapeziusBilling] = useState(false);

  const { accessToken } = useContext(AppContext);
  const toast = useToast();

  const fetchConfig = async () => {
    if (!accessToken) return;
    
    setIsLoading(true);
    try {
      const response = await callApi(`/api/admin/config/org/ionm-general-rules`, accessToken);
      if (response.ok) {
        const data = await response.json();
        const config = data.ionm_general_rules || {};
        
        // Populate the form
        setBillAttemptedMep(config.bill_attempted_mep_monitoring || false);
        setFlagMissingPayer(config.flag_missing_payer || false);
        setAllowDualTrapeziusBilling(config.allow_dual_trapezius_billing || false);
      }
    } catch (error) {
      toast({
        title: 'Error fetching configuration',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [accessToken, toast]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        ionm_general_rules: {
          bill_attempted_mep_monitoring: billAttemptedMep,
          flag_missing_payer: flagMissingPayer,
          allow_dual_trapezius_billing: allowDualTrapeziusBilling
        }
      };

      const response = await callApi(`/api/admin/config/org/ionm-general-rules`, accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save configuration');
      }

      toast({
        title: 'Configuration saved',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
    } catch (error) {
      toast({
        title: 'Error saving configuration',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" p={10}>
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  return (
    <Box bg="white" borderRadius="md" boxShadow="sm" borderWidth="1px" p={6}>
      <VStack spacing={8} align="stretch" maxW="800px">
        
        <Box>
          <Heading size="md" mb={2} color="gray.700">General IONM Rules</Heading>
          <Text fontSize="sm" color="gray.500" mb={6}>
            Configure global coding rules for IONM cases.
          </Text>

          <VStack spacing={6} align="stretch">
            <FormControl display="flex" alignItems="center">
              <Box flex="1">
                <FormLabel htmlFor="bill-attempted" mb="0">
                  Code MEPs When Testing is Run
                </FormLabel>
                <FormHelperText mt={1}>
                  Do not mark MEP codes as 'needs review' if the setup and testing were performed.
                </FormHelperText>
              </Box>
              <Switch 
                id="bill-attempted" 
                isChecked={billAttemptedMep}
                onChange={(e) => setBillAttemptedMep(e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
            </FormControl>

            <Divider />

            <FormControl display="flex" alignItems="center">
              <Box flex="1">
                <FormLabel htmlFor="flag-missing-payer" mb="0">
                  Flag Missing Payer
                </FormLabel>
                <FormHelperText mt={1}>
                  Automatically flag all codes as 'preliminary' (needs review) if the payer is missing or not provided.
                </FormHelperText>
              </Box>
              <Switch 
                id="flag-missing-payer" 
                isChecked={flagMissingPayer}
                onChange={(e) => setFlagMissingPayer(e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
            </FormControl>

            <Divider />

            <FormControl display="flex" alignItems="center">
              <Box flex="1">
                <FormLabel htmlFor="allow-dual-trapezius" mb="0">
                  Allow Dual Trapezius Billing (Spinal Surgeries)
                </FormLabel>
                <FormHelperText mt={1}>
                  When listed twice in the documentation, it means the testing was performed separately for the cranial nerve and the extremity. Allow billing for both.
                </FormHelperText>
              </Box>
              <Switch 
                id="allow-dual-trapezius" 
                isChecked={allowDualTrapeziusBilling}
                onChange={(e) => setAllowDualTrapeziusBilling(e.target.checked)}
                colorScheme="blue"
                size="lg"
              />
            </FormControl>

            <Divider />

            <Button 
              colorScheme="blue" 
              onClick={handleSave} 
              isLoading={isSaving}
              alignSelf="flex-start"
            >
              Save Configuration
            </Button>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default IonmGeneralRulesConfig;
